import React from 'react';
import { Switch } from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';

import useTestMode from '../../models/useTestMode';

import { useClientDetails } from './useClientDetails';

/** RED when TEST is selected — the pill state must read at a glance. */
export const TEST_PILL_RED = '#cf1322';

/**
 * The chrome switch: TEST/LIVE pill for one client's lens. Lives in the
 * banner and (for isDeveloper clients) the navbar — always tappable on
 * mobile, no burger menu required. Flip reloads in place so every screen
 * rebuilds in the new world.
 */
export const ClientTestModePill = ({ clientId }) => {
  const [on, setOn] = useTestMode(clientId);
  if (!clientId) return null;
  return (
    <Switch
      checked={on}
      checkedChildren="TEST"
      unCheckedChildren="LIVE"
      style={on ? { backgroundColor: TEST_PILL_RED } : undefined}
      onChange={(next) => { setOn(next); window.location.reload(); }}
      data-testid="client-test-mode-pill"
    />
  );
};

/** Test Mode chrome, two shades so the SURFACE also reads at a glance:
 * admin wears volcano orange; client/manage wears a redder shade. Both
 * unmistakably "test", never confusable with each other or admin indigo. */
export const TEST_MODE_COLOR = '#fa541c'; // admin surfaces
export const TEST_MODE_CLIENT_COLOR = '#ff3366'; // client/manage: magenta-family alert red — harmonises with #ff0098

/**
 * Always-visible reminder that this browser is looking at the TEST world.
 * Mounted in AccountProfileLayout (same pattern as DelegatedViewingBanner)
 * so it rides every page of the client. Truthful for ANY client: if the
 * lens is test (partner's own switch, or a Strike Admin inspecting them),
 * the purple shows.
 */
export const TestModeBanner = ({ client: clientProp, clientId = null }) => {
  const loadedClient = useClientDetails(clientProp ? null : clientId);
  const client = clientProp || loadedClient;
  const id = clientId || (client && client.id) || null;
  const [on] = useTestMode(id);

  if (!on) return null;

  return (
    <div
      data-testid="test-mode-banner"
      style={{
        position: 'sticky', top: 0, zIndex: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        padding: '6px 16px', background: '#fff0f4', borderBottom: `2px solid ${TEST_MODE_CLIENT_COLOR}`,
        fontSize: 14,
      }}
    >
      <ExperimentOutlined style={{ color: TEST_MODE_CLIENT_COLOR }} />
      <span>
        <b style={{ color: TEST_MODE_CLIENT_COLOR }}>TEST MODE</b>
        {' — showing test data only; payments here are not real.'}
      </span>
      <ClientTestModePill clientId={id} />
    </div>
  );
};

export default TestModeBanner;
